import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Autocomplete,
    TextField,
    Box,
    Checkbox,
    IconButton,
    Tooltip,
} from '@mui/material';
import { MdCheckBoxOutlineBlank, MdCheckBox, MdLinkOff } from 'react-icons/md';
import { FiLink, FiTrash2, FiExternalLink } from 'react-icons/fi';
import ApiRequestService from '../ApiRequestService';
import { NotificationManager } from 'react-notifications';
import SFCustomTable from './SFCustomTable';
import SFConfirmAlert from './SFConfirmAlert';
import { BeatLoader } from 'react-spinners';

const icon = <MdCheckBoxOutlineBlank size={18} />;
const checkedIcon = <MdCheckBox size={18} />;


const LinkQuestionDialog = ({ open, onClose, questionData, linkedQuestions = [] }) => {

    const [subjects, setSubjects] = useState([]);
    const [topics, setTopics] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);

    const [selectedSubject, setSelectedSubject] = useState(null);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubcategories, setSelectedSubcategories] = useState([]);

    const [loading, setLoading] = useState(false);

    const mappedSubcategories = useMemo(() => {
        const isSamePath = questionData
            && selectedSubject?.id === questionData.subject_id
            && selectedTopic?.id === questionData.topic_id
            && selectedCategory?.id === questionData.category_id;

        return subcategories?.filter(sc =>
            !(isSamePath && sc.subcategory_id === questionData.subcategory_id)
        ).map(sc => ({
            id: sc.subcategory_id,
            label: sc.subcategory_name,
            data: sc,
        })) || [];
    }, [subcategories, selectedSubject, selectedTopic, selectedCategory, questionData]);

    useEffect(() => {
        if (open) {
            getSubjects();
        } else {
            resetAll();
        }
    }, [open]);

    const resetAll = () => {
        setSubjects([]);
        setTopics([]);
        setCategories([]);
        setSubcategories([]);
        setSelectedSubject(null);
        setSelectedTopic(null);
        setSelectedCategory(null);
        setSelectedSubcategories([]);
    };

    const getSubjects = () => {
        ApiRequestService({
            method: 'post',
            url: '/getAllSubjects',
        }).then(res => {
            if (res && res.data && res.data.data) {
                setSubjects(res.data.data.subject_names);
            }
        }).catch(() => {
            NotificationManager.error('Failed to load subjects', 'ERROR', 3000);
        });
    };

    const getTopics = (payload) => {
        ApiRequestService({
            method: 'post',
            url: '/getAllTopics',
            data: payload,
        }).then(res => {
            if (res && res.data && res.data.data) {
                setTopics(res.data.data.topic_names);
            }
        }).catch(() => {
            NotificationManager.error('Failed to load topics', 'ERROR', 3000);
        });
    };

    const getCategories = (payload) => {
        ApiRequestService({
            method: 'post',
            url: '/getAllCategories',
            data: payload,
        }).then(res => {
            if (res && res.data && res.data.data) {
                setCategories(res.data.data.category_names);
            }
        }).catch(() => {
            NotificationManager.error('Failed to load categories', 'ERROR', 3000);
        });
    };

    const getSubcategories = (payload) => {
        ApiRequestService({
            method: 'post',
            url: '/getAllSubCategories',
            data: payload,
        }).then(res => {
            if (res && res.data && res.data.data) {
                setSubcategories(res.data.data.subCategory_names);
            }
        }).catch(() => {
            NotificationManager.error('Failed to load subcategories', 'ERROR', 3000);
        });
    };

    const handleSubjectSelect = (_, value) => {
        setTopics([]);
        setCategories([]);
        setSubcategories([]);
        setSelectedTopic(null);
        setSelectedCategory(null);
        setSelectedSubcategories([]);
        setSelectedSubject(value);
        if (value == null) return;
        getTopics({ subject_id: value.id });
    };

    const handleTopicSelect = (_, value) => {
        setCategories([]);
        setSubcategories([]);
        setSelectedCategory(null);
        setSelectedSubcategories([]);
        setSelectedTopic(value);
        if (value == null) return;
        getCategories({
            subject_id: value.data.subject_id,
            topic_id: value.data.topic_id,
        });
    };

    const handleCategorySelect = (_, value) => {
        setSubcategories([]);
        setSelectedSubcategories([]);
        setSelectedCategory(value);
        if (value == null) return;
        getSubcategories({
            subject_id: value.data.subject_id,
            topic_id: value.data.topic_id,
            category_id: value.data.category_id,
        });
    };

    const handleSubcategoriesSelect = (_, values) => {
        if (!values || !questionData) {
            setSelectedSubcategories(values || []);
            return;
        }

        const alreadyLinkedKeys = new Set();
        if (linkedQuestions && linkedQuestions.length) {
            linkedQuestions.forEach(lq => {
                alreadyLinkedKeys.add(`${lq.subject_id}_${lq.topic_id}_${lq.category_id}_${lq.subcategory_id}`);
            });
        }

        const filtered = [];
        let alreadyLinkedBlocked = false;

        values.forEach(sc => {
            const key = `${selectedSubject.id}_${selectedTopic.id}_${selectedCategory.id}_${sc.id}`;
            if (alreadyLinkedKeys.has(key)) {
                alreadyLinkedBlocked = true;
            } else {
                filtered.push(sc);
            }
        });

        if (alreadyLinkedBlocked) {
            NotificationManager.error('Question is already linked to this subcategory', 'ERROR', 3000);
        }

        setSelectedSubcategories(filtered);
    };

    const handleLink = () => {
        if (!selectedSubject || !selectedTopic || !selectedCategory || selectedSubcategories.length === 0) {
            NotificationManager.warning('Please select all fields', 'Warning', 3000);
            return;
        }

        const payload = {
            main_question: questionData,
            link_arr: selectedSubcategories.map(sc =>
                `${selectedSubject.id}_${selectedTopic.id}_${selectedCategory.id}_${sc.id}`
            ),
        };

        setLoading(true);
        ApiRequestService({
            method: 'post',
            url: '/duplicateQuestionAndLink',
            data: payload,
        }).then(res => {
            setLoading(false);
            if (res && res.data && res.data.message !== null && res.data.message !== '') {
                NotificationManager.error(res.data.message, 'ERROR', 3000);
                return;
            }
            NotificationManager.success('Question linked successfully', 'Success', 3000);
            onClose(true);
        }).catch(() => {
            setLoading(false);
            NotificationManager.error('Failed to link question', 'ERROR', 3000);
        });
    };

    const isFormValid = selectedSubject && selectedTopic && selectedCategory && selectedSubcategories.length > 0;

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                onClose(false);
            }}
            maxWidth="sm"
            fullWidth
            disableScrollLock
            PaperProps={{
                style: {
                    borderTop: '6px solid #bb0000',
                    overflow: 'visible',
                },
            }}
            sx={{ zIndex: 1400 }}
        >
            <DialogTitle sx={{ fontWeight: 600 }}>
                Link Question
            </DialogTitle>
            <DialogContent sx={{ overflow: 'visible' }}>
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                    <Autocomplete
                        size="small"
                        options={subjects ? subjects.map(subject => ({
                            id: subject.subject_id,
                            label: subject.subject_name,
                            data: subject,
                        })) : []}
                        value={selectedSubject}
                        onChange={handleSubjectSelect}
                        getOptionLabel={(option) => option.label}
                        componentsProps={{ popper: { style: { zIndex: 1500 } } }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Select Subject"
                                InputProps={{
                                    ...params.InputProps,
                                    sx: { borderRadius: '6px' },
                                }}
                            />
                        )}
                    />

                    <Autocomplete
                        size="small"
                        options={topics ? topics.map(topic => ({
                            id: topic.topic_id,
                            label: topic.topic_name,
                            data: topic,
                        })) : []}
                        value={selectedTopic}
                        onChange={handleTopicSelect}
                        getOptionLabel={(option) => option.label}
                        disabled={!selectedSubject}
                        componentsProps={{ popper: { style: { zIndex: 1500 } } }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Select Topic"
                                InputProps={{
                                    ...params.InputProps,
                                    sx: { borderRadius: '6px' },
                                }}
                            />
                        )}
                    />

                    <Autocomplete
                        size="small"
                        options={categories ? categories.map(category => ({
                            id: category.category_id,
                            label: category.category_name,
                            data: category,
                        })) : []}
                        value={selectedCategory}
                        onChange={handleCategorySelect}
                        getOptionLabel={(option) => option.label}
                        disabled={!selectedTopic}
                        componentsProps={{ popper: { style: { zIndex: 1500 } } }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Select Category"
                                InputProps={{
                                    ...params.InputProps,
                                    sx: { borderRadius: '6px' },
                                }}
                            />
                        )}
                    />

                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        size="small"
                        options={mappedSubcategories}
                        value={selectedSubcategories}
                        onChange={handleSubcategoriesSelect}
                        getOptionLabel={(option) => option.label}
                        disabled={!selectedCategory}
                        componentsProps={{ popper: { style: { zIndex: 1500 } } }}
                        renderOption={(props, option, { selected }) => (
                            <li {...props}>
                                <Checkbox
                                    icon={icon}
                                    checkedIcon={checkedIcon}
                                    style={{ marginRight: 8 }}
                                    checked={selected}
                                />
                                {option.label}
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Select Subcategories"
                                InputProps={{
                                    ...params.InputProps,
                                    sx: { borderRadius: '6px' },
                                }}
                            />
                        )}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    onClick={() => onClose(false)}
                    variant="outlined"
                    sx={{ color: '#bb0000', borderColor: '#bb0000' }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleLink}
                    variant="contained"
                    disabled={!isFormValid || loading}
                    sx={{ backgroundColor: '#bb0000', '&:hover': { backgroundColor: '#990000' } }}
                >
                    {loading ? 'Processing...' : 'Duplicate & Link'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const DuplicateQuestionAndLink = ({ open, onClose, questionData }) => {

    const [linkedQuestions, setLinkedQuestions] = useState([]);
    const [loadingTable, setLoadingTable] = useState(false);
    const [linkDialogOpen, setLinkDialogOpen] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null); // 'unlink' | 'unlinkAndDelete' | null
    const [confirmRow, setConfirmRow] = useState(null);
    const [confirmLoading, setConfirmLoading] = useState(false);
    // Tracks whether any link/unlink happened, so the parent list refreshes on close
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        if (open && questionData) {
            fetchLinkedQuestions();
        }
        if (!open) {
            setLinkedQuestions([]);
            setHasChanges(false);
        }
    }, [open]);

    const fetchLinkedQuestions = () => {
        setLoadingTable(true);
        let questionKey = `${questionData.subject_id}_${questionData.topic_id}_${questionData.category_id}_${questionData.subcategory_id}_${questionData.question_id}`;
        
        ApiRequestService({
            method: 'post',
            url: '/getLinkedQuestions',
            data: { 
                parent_question_key: questionKey,
            },
        }).then(res => {
            setLoadingTable(false);
            if (res && res.data && res.data.data) {
                console.debug("res.data.data", res.data.data)
                setLinkedQuestions(res.data.data);
            }
        }).catch(() => {
            setLoadingTable(false);
            NotificationManager.error('Failed to load linked questions', 'ERROR', 3000);
        });
    };

    const openConfirm = (action, row, index) => {
        setConfirmAction(action);
        setConfirmRow({ ...row, _index: index });
    };

    const closeConfirm = () => {
        setConfirmAction(null);
        setConfirmRow(null);
        setConfirmLoading(false);
    };

    const removeLinkedQuestion = (index) => {
        const updated = [...linkedQuestions];
        updated.splice(index, 1);
        setLinkedQuestions(updated);
    };

    const handleUnlink = () => {
        if (!confirmRow) return;
        console.debug("confirmRow>>?", confirmRow);

        let parentQuestionKey = `${questionData.subject_id}_${questionData.topic_id}_${questionData.category_id}_${questionData.subcategory_id}_${questionData.question_id}`;
        let childQuestionKey = `${confirmRow.subject_id}_${confirmRow.topic_id}_${confirmRow.category_id}_${confirmRow.subcategory_id}_${confirmRow.question_id}`;
        setConfirmLoading(true);
        ApiRequestService({
            method: 'post',
            url: '/unlinkQuestion',
            data: {
                parent_question_key: parentQuestionKey,
                child_question_key: childQuestionKey,
            },
        }).then(res => {
            setConfirmLoading(false);
            if (res && res.data && res.data.message !== null && res.data.message !== '') {
                NotificationManager.error(res.data.message, 'ERROR', 3000);
                closeConfirm();
                return;
            }
            NotificationManager.success('Question unlinked successfully', 'Success', 3000);
            setHasChanges(true);
            removeLinkedQuestion(confirmRow._index);
            closeConfirm();
        }).catch(() => {
            setConfirmLoading(false);
            NotificationManager.error('Failed to unlink question', 'ERROR', 3000);
            closeConfirm();
        });
    };

    const handleUnlinkAndDelete = () => {
        if (!confirmRow) return;

        let parentQuestionKey = `${questionData.subject_id}_${questionData.topic_id}_${questionData.category_id}_${questionData.subcategory_id}_${questionData.question_id}`;
        let childQuestionKey = `${confirmRow.subject_id}_${confirmRow.topic_id}_${confirmRow.category_id}_${confirmRow.subcategory_id}_${confirmRow.question_id}`;

        setConfirmLoading(true);
        ApiRequestService({
            method: 'post',
            url: '/unlinkAndDeleteQuestion',
            data: {
                parent_question_key: parentQuestionKey,
                child_question_key: childQuestionKey,
            },
        }).then(res => {
            setConfirmLoading(false);
            if (res && res.data && res.data.message !== null && res.data.message !== '') {
                NotificationManager.error(res.data.message, 'ERROR', 3000);
                closeConfirm();
                return;
            }
            NotificationManager.success('Question unlinked and deleted successfully', 'Success', 3000);
            setHasChanges(true);
            removeLinkedQuestion(confirmRow._index);
            closeConfirm();
        }).catch(() => {
            setConfirmLoading(false);
            NotificationManager.error('Failed to unlink and delete question', 'ERROR', 3000);
            closeConfirm();
        });
    };

    const handleLinkDialogClose = (linked) => {
        setLinkDialogOpen(false);
        if (linked) {
            setHasChanges(true);
            fetchLinkedQuestions();
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={(_, reason) => {
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                    onClose(hasChanges);
                }}
                maxWidth="md"
                fullWidth
                disableScrollLock
                PaperProps={{
                    style: {
                        borderTop: '6px solid #bb0000',
                        maxHeight: '80vh',
                    },
                }}
            >
                <DialogTitle sx={{ fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Linked Questions
                    <Button
                        variant="contained"
                        startIcon={<FiLink />}
                        onClick={() => setLinkDialogOpen(true)}
                        sx={{ backgroundColor: '#bb0000', '&:hover': { backgroundColor: '#990000' } }}
                    >
                        Link Question
                    </Button>
                </DialogTitle>
                <DialogContent>
                    <table className='linkedquestions-table'>
                        <tr>
                            <th>ID</th>
                            <th>Question</th>
                            <th>Subject</th>
                            <th>Topic</th>
                            <th>Category</th>
                            <th>Subcategory</th>
                            <th>Actions</th>
                        </tr>
                        
                        {!loadingTable && linkedQuestions && linkedQuestions.length ? 
                            linkedQuestions.map((linkedQuestion, index) => {
                                let questionLink = `${window.location.protocol}//${window.location.host}/SubCategoryDetails?topic_id=${linkedQuestion.topic_id}&subject_id=${linkedQuestion.subject_id}&category_id=${linkedQuestion.category_id}&subject_name=${encodeURIComponent(linkedQuestion.subject_name)}&topic_name=${encodeURIComponent(linkedQuestion.topic_name)}&category_name=${encodeURIComponent(linkedQuestion.category_name)}&archive_mode=false&archive_id=1&question_id=${linkedQuestion.question_id}&subcategory_id=${linkedQuestion.subcategory_id}&subcategory_name=${encodeURIComponent(linkedQuestion.subcategory_name)}`;
                                return (
                                    <tr key={index}>
                                        <td>{linkedQuestion.question_id}</td>
                                        <td>{linkedQuestion.question_name}</td>
                                        <td>{linkedQuestion.subject_name}</td>
                                        <td>{linkedQuestion.topic_name}</td>
                                        <td>{linkedQuestion.category_name}</td>
                                        <td>{linkedQuestion.subcategory_name}</td>
                                        <td>
                                            <Box display="flex" gap={0.5} justifyContent="center">
                                                <Tooltip arrow title="Open Question" placement="top">
                                                    <IconButton
                                                        onClick={() => window.open(questionLink, '_blank')}
                                                        sx={{ color: '#5d78ac', '&:hover': { backgroundColor: '#e3f2fd' } }}
                                                    >
                                                        <FiExternalLink size={20} />
                                                    </IconButton>
                                                </Tooltip>
                                                {/* <Tooltip arrow title="Unlink" placement="top">
                                                    <IconButton
                                                        onClick={() => openConfirm('unlink', linkedQuestion, index)}
                                                        sx={{ color: '#ed6c02', '&:hover': { backgroundColor: '#fff3e0' } }}
                                                    >
                                                        <MdLinkOff size={20} />
                                                    </IconButton>
                                                </Tooltip> */}
                                                <Tooltip arrow title="Unlink & Delete" placement="top">
                                                    <IconButton
                                                        onClick={() => openConfirm('unlinkAndDelete', linkedQuestion, index)}
                                                        sx={{ color: '#d32f2f', '&:hover': { backgroundColor: '#ffebee' } }}
                                                    >
                                                        <FiTrash2 size={20} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </td>
                                    </tr>
                                )
                            })
                        : 
                        <tr>
                            <td colSpan={7}>
                                <div style={{textAlign:"center"}}>
                                    {loadingTable ? 
                                    <BeatLoader loading color='#bb0000' />
                                    : "No Linked Questions"}
                                </div>
                            </td>
                        </tr>
                        }
                    </table>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => onClose(hasChanges)}
                        variant="outlined"
                        sx={{ color: '#bb0000', borderColor: '#bb0000' }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <LinkQuestionDialog
                open={linkDialogOpen}
                onClose={handleLinkDialogClose}
                questionData={questionData}
                linkedQuestions={linkedQuestions}
            />

            <SFConfirmAlert
                open={confirmAction === 'unlink'}
                title="Unlink Question"
                description="This will unlink the question from the current question. This action cannot be reverted and the question cannot be linked again."
                onConfirm={handleUnlink}
                onCancel={closeConfirm}
                loading={confirmLoading}
            />

            <SFConfirmAlert
                open={confirmAction === 'unlinkAndDelete'}
                title="Unlink & Delete Question"
                description="This will unlink and permanently delete the linked question from the system. This action cannot be reverted."
                onConfirm={handleUnlinkAndDelete}
                onCancel={closeConfirm}
                loading={confirmLoading}
            />
        </>
    );
};

export default DuplicateQuestionAndLink;
